// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createSlice, type PayloadAction, type Update } from '@reduxjs/toolkit';
import {
  advisorsAdapter,
  initialState,
  institutionsAdapter,
  pendingInstitutionsAdapter,
  type ClientsState,
  type InstitutionWithClientId,
  type SelectedClient,
} from './clients-state';
import {
  type AdvisoryInstitution,
  type AdvisoryProposalStatus,
  type Advisor,
  type CreateAdvisoryAccountsCitecRequestBody,
} from '@shared/data-access/types/brokerage-types';
import type { UpdateAdvisoryAccountsCitecRequestBody } from '@shared/data-access/types/brokerage/advisory-accounts-citec';

const removeInstitutionsForClient = (state: ClientsState, client_id: number) => {
  const idsToRemove = state.institutions.ids.filter(id => {
    const entity = state.institutions.entities[id as string];

    return entity?.client_id === client_id;
  });

  institutionsAdapter.removeMany(state.institutions, idsToRemove);
};

const replaceInstitutionsForClient = (
  state: ClientsState,
  client_id: number,
  institutions: AdvisoryInstitution[],
) => {
  removeInstitutionsForClient(state, client_id);

  if (institutions.length === 0) {
    return;
  }

  institutionsAdapter.addMany(
    state.institutions,
    institutions.map(institution => ({ ...institution, client_id })),
  );
};

export const clientsSlice = createSlice({
  name: 'clients',
  initialState: () => initialState,
  reducers: {
    reset: () => initialState,

    setBankUploadDraft: (
      state,
      { payload }: PayloadAction<CreateAdvisoryAccountsCitecRequestBody>,
    ) => {
      state.bankUploadDraft = payload;
    },

    clearBankUploadDraft: state => {
      state.bankUploadDraft = null;
    },

    setClient: (state, { payload }: PayloadAction<SelectedClient | null>) => {
      state.client = payload;
    },

    patchClientStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        id: number;
        status: AdvisoryProposalStatus | null;
      }>,
    ) => {
      if (state.client?.id !== payload.id) {
        return;
      }

      state.client.status = payload.status;
    },

    setInstitutions: (
      state,
      {
        payload,
      }: PayloadAction<{ client_id: number; institutions: AdvisoryInstitution[] }>,
    ) => {
      replaceInstitutionsForClient(state, payload.client_id, payload.institutions);
    },

    addPendingInstitution: (
      state,
      { payload }: PayloadAction<AdvisoryInstitution>,
    ) => {
      pendingInstitutionsAdapter.addOne(state.pendingInstitutions, payload);
    },

    removePendingInstitution: (state, { payload }: PayloadAction<string>) => {
      pendingInstitutionsAdapter.removeOne(state.pendingInstitutions, payload);
    },

    removePendingInstitutions: (state, { payload }: PayloadAction<string[]>) => {
      pendingInstitutionsAdapter.removeMany(state.pendingInstitutions, payload);
    },

    removeInstitution: (
      state,
      { payload }: PayloadAction<{ client_id: number; institutionId: string }>,
    ) => {
      const entity = state.institutions.entities[payload.institutionId];

      if (!entity || entity.client_id !== payload.client_id) {
        return;
      }

      institutionsAdapter.removeOne(state.institutions, payload.institutionId);
    },

    removeClientAccount: (
      state,
      {
        payload,
      }: PayloadAction<{
        client_id: number;
        institutionId: string;
        ibans: string[];
      }>,
    ) => {
      const entity = state.institutions.entities[payload.institutionId];

      if (!entity || entity.client_id !== payload.client_id) {
        return;
      }

      const accounts = entity.products.accounts.filter(
        account => !payload.ibans.includes(account.iban),
      );

      if (accounts.length === 0) {
        institutionsAdapter.removeOne(state.institutions, payload.institutionId);

        return;
      }

      institutionsAdapter.updateOne(state.institutions, {
        id: payload.institutionId,
        changes: {
          products: {
            ...entity.products,
            accounts,
          },
        },
      });
    },

    setAdvisors: (state, { payload }: PayloadAction<Advisor[]>) => {
      advisorsAdapter.setAll(state.advisors, payload);
    },

    updateAdvisor: (state, { payload }: PayloadAction<Update<Advisor, number>>) => {
      advisorsAdapter.updateOne(state.advisors, payload);
    },

    removeAdvisorAccount: (
      state,
      {
        payload,
      }: PayloadAction<{
        advisorId: number;
        institutionId: string;
        ibans: string[];
      }>,
    ) => {
      const advisor = state.advisors.entities[payload.advisorId];
      if (!advisor) return;

      const updatedInstitutions = advisor.institutions.map(institution => {
        if (institution.id !== payload.institutionId) {
          return institution;
        }

        return {
          ...institution,
          products: {
            ...institution.products,
            accounts: institution.products.accounts.filter(
              account => !payload.ibans.includes(account.iban),
            ),
          },
        };
      });

      const filteredInstitutions = updatedInstitutions.filter(
        institution => institution.products.accounts.length > 0,
      );

      advisorsAdapter.updateOne(state.advisors, {
        id: payload.advisorId,
        changes: {
          institutions: filteredInstitutions,
        },
      });
    },

    updateClientAccountStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        ibans: string[];
        confirm: boolean;
      }>,
    ) => {
      const toRemove: string[] = [];
      const updates: Update<InstitutionWithClientId, string>[] = [];

      for (const id of state.institutions.ids) {
        const institution = state.institutions.entities[id as string];

        if (!institution) {
          continue;
        }

        const hasPendingAccounts = institution.products.accounts.some(
          account =>
            payload.ibans.includes(account.iban) && account.status === 'pending',
        );

        if (!hasPendingAccounts) {
          continue;
        }

        if (payload.confirm) {
          updates.push({
            id: id as string,
            changes: {
              products: {
                ...institution.products,
                accounts: institution.products.accounts.map(account =>
                  payload.ibans.includes(account.iban) &&
                  account.status === 'pending'
                    ? { ...account, status: 'active' as const }
                    : account,
                ),
              },
            },
          });

          continue;
        }

        const accounts = institution.products.accounts.filter(
          account =>
            !(payload.ibans.includes(account.iban) && account.status === 'pending'),
        );

        if (accounts.length === 0) {
          toRemove.push(id as string);
        } else {
          updates.push({
            id: id as string,
            changes: {
              products: {
                ...institution.products,
                accounts,
              },
            },
          });
        }
      }

      if (updates.length > 0) {
        institutionsAdapter.updateMany(state.institutions, updates);
      }

      if (toRemove.length > 0) {
        institutionsAdapter.removeMany(state.institutions, toRemove);
      }
    },

    patchManualInstitutionData: (
      state,
      { payload }: PayloadAction<UpdateAdvisoryAccountsCitecRequestBody>,
    ) => {
      const institutionId = payload.institution.id;
      const accountPatches = payload.accounts ?? [];
      const entity = state.institutions.entities[institutionId];

      if (!entity) {
        return;
      }

      const patchedInstitution: AdvisoryInstitution = {
        ...entity,
        name: payload.institution.name,
        logo_url: payload.institution.logo_url ?? entity.logo_url ?? null,
      };

      const patchedEntity =
        accountPatches.length === 0
          ? patchedInstitution
          : {
              ...patchedInstitution,
              products: {
                ...patchedInstitution.products,
                accounts: patchedInstitution.products.accounts.map(account => {
                  const patch = accountPatches.find(
                    row => row.iban === account.iban,
                  );

                  if (!patch) {
                    return account;
                  }

                  const available =
                    patch.available ?? account.available ?? account.cash ?? null;

                  return {
                    ...account,
                    name: patch.name,
                    currency: patch.currency,
                    type: patch.account_type,
                    available,
                    cash: available,
                    owners: patch.owners?.map(owner => ({
                      name: owner.name ?? null,
                      role: owner.role ?? null,
                    })),
                    valid_from: patch.balance_date
                      ? `${patch.balance_date}T00:00:00.000Z`
                      : account.valid_from,
                  };
                }),
              },
            };

      institutionsAdapter.updateOne(state.institutions, {
        id: institutionId,
        changes: patchedEntity,
      });
    },
  },
});
